/**
 * Vue Router 配置 (懒加载优化版)
 *
 * 优化说明:
 * - 所有路由使用懒加载 (() => import())
 * - 登录后预加载常用页面 (lobby, home)
 * - 游戏页面标记为 prefetch,浏览器空闲时预加载
 */

import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
// 纯变量模块, 零依赖, 静态引入不会增加大厅主 chunk 体积 (不像 stores/websocket 那些重的懒加载模块)。
// 守卫需要在 next() 之前同步拿到这个值来决定放行还是重定向, 动态 import() 的微任务延迟会导致
// next('/') 抢在判断结果出来之前就执行, 所以这里不能跟别的重模块一样用动态 import。
import { hasPendingLiveLogin } from '@/composables/liveLoginBridge'

// 路由配置
const routes: RouteRecordRaw[] = [
  // ==================== 游戏总大厅 ====================
  {
    path: '/',
    name: 'main-lobby',
    component: () => import('@/views/main-lobby/index.vue'),
    meta: {
      title: '游戏大厅',
      requiresAuth: false,
    },
  },

  // ==================== 大厅子页面 (活动中心 / 我的中心) ====================
  {
    path: '/promotions',
    name: 'promotions',
    component: () => import('@/views/main-lobby/LobbyPromotions.vue'),
    meta: {
      title: '活动中心',
      requiresAuth: true,
      keepAlive: true, // 缓存组件: 保留滚动/状态, 返回时 onActivated 刷新数据
    },
  },
  {
    path: '/me',
    name: 'my-center',
    component: () => import('@/views/main-lobby/LobbyMyCenter.vue'),
    meta: {
      title: '我的',
      requiresAuth: true,
    },
  },

  // ==================== 我的中心子页面 ====================
  {
    path: '/lobby/invite',
    name: 'lobby-invite',
    component: () => import('@/views/main-lobby/LobbyInviteFriends.vue'),
    meta: {
      title: '邀请好友',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/deposit',
    name: 'lobby-deposit',
    component: () => import('@/views/main-lobby/LobbyDeposit.vue'),
    meta: {
      title: '充值',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/deposit-orders',
    name: 'lobby-deposit-orders',
    component: () => import('@/views/main-lobby/LobbyDepositOrders.vue'),
    meta: {
      title: '充值记录',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/withdraw',
    name: 'lobby-withdraw',
    component: () => import('@/views/main-lobby/LobbyWithdraw.vue'),
    meta: {
      title: '提现',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/withdraw/detail/:orderNo',
    name: 'lobby-withdraw-detail',
    component: () => import('@/views/main-lobby/LobbyWithdrawDetail.vue'),
    meta: {
      title: '提现详情',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/withdraw-orders',
    name: 'lobby-withdraw-orders',
    component: () => import('@/views/main-lobby/LobbyWithdrawOrders.vue'),
    meta: {
      title: '提现记录',
      requiresAuth: true,
    },
  },
  {
    // /lobby/vip 旧详情页已合并到 /lobby/vip-list, 兼容老链接重定向
    path: '/lobby/vip',
    redirect: '/lobby/vip-list',
  },
  {
    path: '/lobby/vip-list',
    name: 'lobby-vip-list',
    component: () => import('@/views/main-lobby/LobbyVipList.vue'),
    meta: {
      title: 'VIP等级列表',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/vip-rebate',
    name: 'lobby-vip-rebate',
    component: () => import('@/views/main-lobby/LobbyVipRebate.vue'),
    meta: {
      title: 'VIP反水比例',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/team',
    name: 'lobby-team',
    component: () => import('@/views/main-lobby/LobbyTeamData.vue'),
    meta: {
      title: '团队数据',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/transactions',
    name: 'lobby-transactions',
    component: () => import('@/views/main-lobby/LobbyTransactions.vue'),
    meta: {
      title: '交易记录',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/bonus-orders',
    name: 'lobby-bonus-orders',
    component: () => import('@/views/main-lobby/LobbyBonusOrders.vue'),
    meta: {
      title: '赠金记录',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/rebate',
    name: 'lobby-rebate',
    component: () => import('@/views/main-lobby/LobbyRebate.vue'),
    meta: {
      title: '反水',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/bet-records',
    name: 'lobby-bet-records',
    component: () => import('@/views/main-lobby/LobbyBetRecords.vue'),
    meta: {
      title: '投注记录',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/bank-cards',
    name: 'lobby-bank-cards',
    component: () => import('@/views/main-lobby/LobbyBankCards.vue'),
    meta: {
      title: '银行卡管理',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/bank-cards/add',
    name: 'lobby-add-bank-card',
    component: () => import('@/views/main-lobby/LobbyAddBankCard.vue'),
    meta: {
      title: '添加银行卡',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/favorites',
    name: 'lobby-favorites',
    component: () => import('@/views/main-lobby/LobbyFavorites.vue'),
    meta: {
      title: '我的收藏',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/section/:code',
    name: 'lobby-section',
    component: () => import('@/views/main-lobby/SectionGames.vue'),
    meta: {
      title: '游戏列表',
      requiresAuth: false,
    },
  },
  {
    path: '/provider-games/:providerCode',
    name: 'provider-games',
    component: () => import('@/views/main-lobby/ProviderGames.vue'),
    meta: {
      title: '游戏列表',
      requiresAuth: true,
      guestAllowed: true, // 试玩可浏览厂牌游戏列表 (具体游戏走 /api/v1/guest/launch)
    },
  },
  {
    path: '/lobby/fund-password',
    name: 'lobby-fund-password',
    component: () => import('@/views/main-lobby/LobbyFundPassword.vue'),
    meta: {
      title: '资金密码',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/settings',
    name: 'lobby-settings',
    component: () => import('@/views/main-lobby/LobbySettings.vue'),
    meta: {
      title: '设置',
      requiresAuth: false,
    },
  },

  // ==================== 代理推广 ====================
  {
    path: '/lobby/agent',
    name: 'lobby-agent',
    component: () => import('@/views/main-lobby/agent/AgentView.vue'),
    meta: {
      title: '代理推广',
      requiresAuth: true,
    },
  },
  {
    path: '/lobby/agent/:tab(kpi|trend|members)',
    redirect: to => ({ path: '/lobby/agent', query: { tab: to.params.tab } }),
  },

  // ==================== 登录 / 注册 (合并页, 内部 tab 切换) ====================
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/AuthPage.vue'),
    meta: {
      title: '登录',
      requiresAuth: false,
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/AuthPage.vue'),
    meta: {
      title: '注册',
      requiresAuth: false,
    },
  },
  {
    path: '/settings/language',
    name: 'settings-language',
    component: () => import('@/views/settings/LanguagePage.vue'),
    meta: {
      title: '语言设置',
      requiresAuth: false,
    },
  },

  // ==================== 视讯游戏 (原有路由加 /live 前缀) ====================
  {
    path: '/live/login',
    name: 'live-login',
    component: () => import('@/views/login/index.vue'),
    meta: {
      title: '视讯登录',
      requiresAuth: false,
    },
  },
  {
    path: '/live/lobby',
    name: 'live-lobby',
    component: () => import('@/views/lobby/index.vue'),
    meta: {
      title: '视讯大厅',
      requiresAuth: true,
      preload: true,
    },
  },
  {
    path: '/live/home',
    name: 'live-home',
    component: () => import('@/views/home/index.vue'),
    meta: {
      title: '台桌列表',
      requiresAuth: true,
      preload: true,
    },
  },
  {
    path: '/live/game/:tableId?',
    name: 'live-game',
    component: () => import(/* webpackPrefetch: true */ '@/views/game/index.vue'),
    meta: {
      title: '游戏',
      requiresAuth: true,
      prefetch: true,
    },
  },

  // ==================== 兼容旧路由 (重定向到新路由) ====================
  {
    path: '/home',
    redirect: '/live/home',
  },
  {
    path: '/game/:tableId?',
    redirect: to => `/live/game/${to.params.tableId || ''}`,
  },
]

// 创建路由实例
const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
})

/**
 * 预加载路由组件
 * @param routeName - 路由名称
 */
function preloadRoute(routeName: string): void {
  const route = routes.find(r => r.name === routeName)
  if (route && route.component) {
    // 检查是否是动态导入函数
    const component = route.component
    if (typeof component === 'function') {
      try {
        // 触发动态导入,但不等待结果
        // 使用类型断言绕过 TypeScript 的类型检查
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = (component as any)()
        // 如果返回 Promise,捕获错误
        if (result && typeof result === 'object' && 'catch' in result) {
          result.catch(() => {
            // 预加载失败不影响用户体验
            console.warn(`[Router] 预加载路由失败: ${routeName}`)
          })
        }
      } catch {
        // 预加载失败不影响用户体验
        console.warn(`[Router] 预加载路由失败: ${routeName}`)
      }
    }
  }
}

/**
 * 预加载标记为 preload 的路由
 */
function preloadMarkedRoutes(): void {
  // 使用 requestIdleCallback 在浏览器空闲时预加载
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        routes.forEach(route => {
          if (route.meta?.preload) {
            preloadRoute(route.name as string)
          }
        })
      },
      { timeout: 2000 } // 最多等待 2 秒
    )
  } else {
    // 降级方案: 使用 setTimeout
    setTimeout(() => {
      routes.forEach(route => {
        if (route.meta?.preload) {
          preloadRoute(route.name as string)
        }
      })
    }, 1000)
  }
}

// 已预加载标记
let hasPreloaded = false

// 视讯"正片"路由 (登录成功之后才会进入的页面, 区别于 live-login)
// 导出给 App.vue 复用, 判断是否需要挂 beforeunload 确认弹窗 + pagehide 尽力退出
export const LIVE_CONTENT_ROUTE_NAMES = new Set(['live-lobby', 'live-home', 'live-game'])

// 是否是应用启动后的第一次导航: 用来判断"直接刷新/直接打开这个 URL" 还是
// "应用内正常跳转过来的" —— 视讯的 WebSocket 连接、台桌数据全部只存在内存里,
// 刷新会把这些清空, 但 token 还有效的话 requiresAuth 检查会照常放行, 结果就是
// 重新渲染出一个 WS 没连、数据全空的"死页面"。刷新必然是这个应用生命周期里的
// 第一次路由解析, 用这个信号比去检查 wsStore.isConnected 更轻量 (不需要为了
// 判断而提前加载视讯 WS 那一整套重模块)。
let isFirstNavigation = true

/**
 * live-login 兜底分支和"刷新落到视讯正片页面"分支共用这一套清理逻辑。
 * 实际实现在 useLiveGamePlay.ts 的 exitLiveAndRestoreLobby(), 这里只是动态
 * import 一下 (避免视讯那一整套重模块被打进路由这个必然随大厅主 chunk 加载的文件)。
 */
function exitLiveSessionAndRestore(): void {
  import('@/composables/useLiveGamePlay').then(({ exitLiveAndRestoreLobby }) => {
    exitLiveAndRestoreLobby()
  })
}

// 路由守卫
router.beforeEach((to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const firstNavigation = isFirstNavigation
  isFirstNavigation = false

  // ==================== Telegram Desktop macOS 特殊处理 ====================
  // Telegram Desktop macOS 会把 tgWebAppData 参数放在 path 中而不是 query/hash
  // 例如: /tgWebAppData=xxx&tgWebAppVersion=8.0...
  // 需要检测并重定向到首页，同时保留参数到 hash 中供 Telegram SDK 使用
  if (to.path.includes('tgWebAppData') || to.fullPath.includes('tgWebAppData')) {
    console.log('[Router] 检测到 Telegram 参数在 path 中，重定向到首页')
    // 将参数保存到 sessionStorage 供后续使用
    const params = to.path.replace(/^\//, '') // 移除开头的 /
    sessionStorage.setItem('tgWebAppParams', params)
    // 重定向到首页
    next({ path: '/', replace: true })
    return
  }

  // 设置页面标题
  if (to.meta.title) {
    const appTitle = window.APP_CONFIG?.appTitle
    document.title = `${to.meta.title} - ${appTitle}`
  }

  // 视讯登录页 (/live/login) 现在只服务 enterLiveAndGo() 的静默自动登录,
  // 不再允许用户看到手动登录表单 (账号密码是后端生成的视讯专属账号, 表单对
  // 玩家没有意义)。这里做前置分流:
  // - 有排队的静默登录凭据 (liveLoginBridge.ts) → 正常入口, 放行, 什么都不清理
  //   (userStore.loggedIn 此时为 true 是因为大厅账号本来就登录着, 不是需要清理的
  //   残留状态, 千万不能在这里断线/登出, 否则会跟静默登录里 onMounted 发起的
  //   连接互相抢)。
  // - 没有排队凭据 (浏览器后退/心跳超时/被踢下线等落到这个路由) → 说明不是正常
  //   入口, 按"退出视讯"处理: 断开视讯 WS、调退出接口回收资金/会话、用大厅 JWT
  //   纠正被视讯登录覆盖的字段, 然后直接回大厅首页, 不渲染这个手动表单。
  if (to.name === 'live-login') {
    // 有排队的静默登录凭据 → enterLiveAndGo() 的正常入口, 直接放行, 不碰 WS/store
    if (hasPendingLiveLogin()) {
      next()
      return
    }

    // 没有排队凭据 (浏览器后退/心跳超时/被踢下线等落到这个路由) → 不是正常入口,
    // 按"退出视讯"处理, 不渲染手动登录表单, 直接回大厅
    exitLiveSessionAndRestore()
    next('/')
    return
  }

  // 刷新/直接打开 /live/lobby、/live/home、/live/game 这几个视讯正片页面:
  // WS 连接和台桌数据已经被刷新清空, token 却还有效, 放行的话会渲染出一个
  // 死页面。按"退出视讯"处理, 直接回大厅 (exitLive() 内部 session_id 为空时
  // 会自己短路跳过退出接口调用, 这里不用先判断是否真的有会话)。
  if (firstNavigation && LIVE_CONTENT_ROUTE_NAMES.has(to.name as string)) {
    exitLiveSessionAndRestore()
    next('/')
    return
  }

  // 检查是否需要登录
  if (to.meta.requiresAuth) {
    // 动态导入 store 和 auth,避免循环依赖
    Promise.all([
      import('@/stores'),
      import('@/services/request'),
      import('@/services/api/guest'),
    ]).then(([{ useUserStore }, { getAccessToken }, { getGuestToken }]) => {
      const userStore = useUserStore()
      const hasToken = !!getAccessToken()
      const hasGuest = !!getGuestToken()

      // 正常登录态 → 直接放行
      if (userStore.loggedIn || hasToken) {
        if (!hasPreloaded) {
          hasPreloaded = true
          preloadMarkedRoutes()
        }
        next()
        return
      }

      // 试玩态 (有 guest_token, 无 JWT) → 仅允许 meta.guestAllowed 的页面
      if (hasGuest) {
        if (to.meta.guestAllowed) {
          next()
          return
        }
        // 不在白名单 → 回首页 (而不是反复进登录页)
        next({ path: '/', replace: true })
        return
      }

      // 未登录也无 guest token → 跳登录页
      const isLiveRoute = to.path.startsWith('/live')
      const loginRoute = isLiveRoute ? 'live-login' : 'login'
      next({ name: loginRoute, query: { redirect: to.fullPath } })
    })
  } else {
    next()
  }
})

export default router
